import { useState, useEffect, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { toast } from 'sonner';
import { useImportConfigurationStore } from '../../stores/import-configuration-store';
import {
  SheetMappingRequest,
  DEFAULT_SHEET_MAPPING,
  validateSheetMapping,
} from '../../types/import-configuration-types';
import { ApplicationType, ApplicationTypeLabels } from '../../types/translation-types';

interface FormData {
  sheetName: string;
  applicationType: ApplicationType | null;
  keyColumnNumber: number;
  descriptionColumnNumber: number | undefined;
}

export function SheetMappingDialog() {
  const {
    sheetMappings,
    sheetFormDialogOpen,
    editingSheetMapping,
    isSubmitting,
    submitError,
    saveSheetMapping,
    closeSheetFormDialog,
    clearSubmitError,
  } = useImportConfigurationStore();

  const isEditMode = editingSheetMapping !== null;

  const [formData, setFormData] = useState<FormData>({
    sheetName: '',
    applicationType: null,
    keyColumnNumber: DEFAULT_SHEET_MAPPING.keyColumnNumber || 1,
    descriptionColumnNumber: DEFAULT_SHEET_MAPPING.descriptionColumnNumber,
  });

  const [validationError, setValidationError] = useState<string | null>(null);

  // Initialize form when dialog opens or editing mapping changes
  useEffect(() => {
    if (sheetFormDialogOpen) {
      clearSubmitError();
      setValidationError(null);

      if (editingSheetMapping) {
        setFormData({
          sheetName: editingSheetMapping.sheetName,
          applicationType: editingSheetMapping.applicationType,
          keyColumnNumber: editingSheetMapping.keyColumnNumber,
          descriptionColumnNumber: editingSheetMapping.descriptionColumnNumber,
        });
      } else {
        setFormData({
          sheetName: '',
          applicationType: null,
          keyColumnNumber: DEFAULT_SHEET_MAPPING.keyColumnNumber || 1,
          descriptionColumnNumber: DEFAULT_SHEET_MAPPING.descriptionColumnNumber,
        });
      }
    }
  }, [sheetFormDialogOpen, editingSheetMapping, clearSubmitError]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Validate
      const request: SheetMappingRequest = {
        mappingId: editingSheetMapping?.mappingId,
        sheetName: formData.sheetName.trim(),
        applicationType: formData.applicationType!,
        keyColumnNumber: formData.keyColumnNumber,
        descriptionColumnNumber: formData.descriptionColumnNumber,
      };

      const error = validateSheetMapping(
        request,
        sheetMappings,
        editingSheetMapping?.mappingId
      );

      if (error) {
        setValidationError(error);
        return;
      }

      setValidationError(null);

      try {
        await saveSheetMapping(request);
        toast.success(
          isEditMode
            ? `Sheet mapping "${formData.sheetName}" updated`
            : `Sheet mapping "${formData.sheetName}" created`
        );
      } catch {
        // Error is handled in store
      }
    },
    [formData, editingSheetMapping, sheetMappings, saveSheetMapping, isEditMode]
  );

  const applicationTypeOptions = Object.entries(ApplicationTypeLabels).map(([value, label]) => ({
    value: Number(value) as ApplicationType,
    label,
  }));

  return (
    <Dialog open={sheetFormDialogOpen} onOpenChange={closeSheetFormDialog}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? 'Edit Sheet Mapping' : 'Add Sheet Mapping'}
            </DialogTitle>
            <DialogDescription>
              Configure how an Excel sheet maps to an application type.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Validation error */}
            {validationError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}

            {/* API error */}
            {submitError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}

            {/* Sheet Name */}
            <div className="space-y-2">
              <Label htmlFor="sheetName">
                Sheet Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="sheetName"
                value={formData.sheetName}
                onChange={e => setFormData(prev => ({ ...prev, sheetName: e.target.value }))}
                placeholder="e.g., Kiosk"
                disabled={isSubmitting}
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground">
                Must match the Excel sheet name exactly (case-sensitive)
              </p>
            </div>

            {/* Application Type */}
            <div className="space-y-2">
              <Label htmlFor="applicationType">
                Application Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.applicationType?.toString() || ''}
                onValueChange={value =>
                  setFormData(prev => ({ ...prev, applicationType: Number(value) as ApplicationType }))
                }
                disabled={isSubmitting}
              >
                <SelectTrigger id="applicationType">
                  <SelectValue placeholder="Select application type" />
                </SelectTrigger>
                <SelectContent>
                  {applicationTypeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value.toString()}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The application this sheet's translations belong to
              </p>
            </div>

            {/* Key Column */}
            <div className="space-y-2">
              <Label htmlFor="keyColumnNumber">
                Key Column <span className="text-red-500">*</span>
              </Label>
              <Input
                id="keyColumnNumber"
                type="number"
                min={1}
                value={formData.keyColumnNumber}
                onChange={e =>
                  setFormData(prev => ({ ...prev, keyColumnNumber: Number(e.target.value) }))
                }
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                Column number containing translation keys (1 = A, 2 = B, etc.)
              </p>
            </div>

            {/* Description Column */}
            <div className="space-y-2">
              <Label htmlFor="descriptionColumnNumber">Description Column</Label>
              <Input
                id="descriptionColumnNumber"
                type="number"
                min={1}
                value={formData.descriptionColumnNumber || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    descriptionColumnNumber: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
                placeholder="Optional"
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                Column number containing translation descriptions (optional)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeSheetFormDialog}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
